import { useState } from 'react';
import {
    submitAssistantVideo,
    checkVideoStatus,
    finaliseVideo,
    submitAssistantDocument // Single endpoint for complete document processing
} from '../api/api';
import { KnowledgeFileType } from '../api/models';

export type FileStepStatus = 'pending' | 'active' | 'complete' | 'error';
export type FileType = KnowledgeFileType;

export interface FileProgress {
    filename: string;
    fileType: FileType;
    currentStep: 'uploading' | 'processing' | 'indexing' | 'complete' | 'error';
    steps: {
        uploading: FileStepStatus;
        processing: FileStepStatus;
        indexing: FileStepStatus;
    };
    errorMessage?: string;
}

export const useFileProcessing = () => {
    const [currentFileProgress, setCurrentFileProgress] = useState<FileProgress | null>(null);
    const [currentFileIndex, setCurrentFileIndex] = useState<number>(0);
    const [totalFiles, setTotalFiles] = useState<number>(0);

    const initialiseFileProgress = (filename: string, fileType: FileType): FileProgress => {
        return {
            filename,
            fileType,
            currentStep: 'uploading',
            steps: {
                uploading: 'pending',
                processing: 'pending',
                indexing: 'pending'
            }
        };
    };

    const startNewFile = (filename: string, fileType: FileType, index: number, total: number) => {
        setCurrentFileIndex(index);
        setTotalFiles(total);
        setCurrentFileProgress(initialiseFileProgress(filename, fileType));
    };

    const updateCurrentFileProgress = (updates: Partial<FileProgress>) => {
        setCurrentFileProgress(prev => prev ? { ...prev, ...updates } : null);
    };

    const setStepStatus = (
        step: keyof FileProgress['steps'],
        status: FileStepStatus
    ) => {
        setCurrentFileProgress(prev =>
            prev ? {
                ...prev,
                steps: { ...prev.steps, [step]: status }
            } : null
        );
    };

    const uploadDocument = async (
        assistantId: string,
        documentFile: File,
        index: number,
        total: number
    ): Promise<{ success: boolean; error?: string }> => {
        const filename = documentFile.name;

        // Initialise progress for this document
        startNewFile(filename, 'document', index, total);

        // Set all steps to active (processing happening on backend)
        setStepStatus('uploading', 'active');
        setStepStatus('processing', 'active');
        setStepStatus('indexing', 'active');
        updateCurrentFileProgress({
            currentStep: 'uploading'
        });

        try {
            // Single call - backend does everything
            const uploadResp = await submitAssistantDocument(
                assistantId,
                documentFile
            );

            if (!uploadResp.ok) {
                // Mark all active steps as error
                setStepStatus('uploading', 'error');
                setStepStatus('processing', 'error');
                setStepStatus('indexing', 'error');
                const errMsg = uploadResp?.error || uploadResp.data?.status || 'Upload failed';
                updateCurrentFileProgress({
                    currentStep: 'error',
                    errorMessage: errMsg
                });
                return { success: false, error: errMsg };
            }

            // All steps complete
            setStepStatus('uploading', 'complete');
            setStepStatus('processing', 'complete');
            setStepStatus('indexing', 'complete');
            updateCurrentFileProgress({
                currentStep: 'complete'
            });

            await new Promise(resolve => setTimeout(resolve, 1500));

            return { success: true };

        } catch (error) {
            setStepStatus('uploading', 'error');
            setStepStatus('processing', 'error');
            setStepStatus('indexing', 'error');
            updateCurrentFileProgress({
                currentStep: 'error',
                errorMessage: error instanceof Error ? error.message : 'Unknown error'
            });
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error'
            };
        }
    };

    const uploadVideo = async (
        assistantId: string,
        videoFile: File,
        index: number,
        total: number
    ): Promise<{ success: boolean; error?: string }> => {
        const filename = videoFile.name;
        const fileSize = videoFile.size;

        // Initialise progress for this video
        startNewFile(filename, 'video', index, total);
        setStepStatus('uploading', 'active');

        try {
            // Phase 1: Upload and trigger
            const uploadResp = await submitAssistantVideo(
                assistantId,
                videoFile
            );

            if (!uploadResp.ok || !uploadResp.data?.video_id) {
                setStepStatus('uploading', 'error');
                updateCurrentFileProgress({
                    currentStep: 'error',
                    errorMessage: 'Upload failed'
                });
                return { success: false, error: 'Upload failed' };
            }

            const videoId = uploadResp.data.video_id;

            // Complete upload step
            setStepStatus('uploading', 'complete');
            setStepStatus('processing', 'active');
            updateCurrentFileProgress({
                currentStep: 'processing'
            });

            // Phase 2: Poll for processing completion
            let isReady = false;
            let attempts = 0;
            const maxAttempts = 240;

            while (!isReady && attempts < maxAttempts) {
                await new Promise(resolve => setTimeout(resolve, 15000));

                const statusResp = await checkVideoStatus(
                    assistantId,
                    videoId
                );

                if (statusResp.data?.status === 'ready') {
                    isReady = true;
                    setStepStatus('processing', 'complete');
                } else if (statusResp.data?.status === 'failed') {
                    setStepStatus('processing', 'error');
                    updateCurrentFileProgress({
                        currentStep: 'error',
                        errorMessage: 'Video processing failed'
                    });
                    return { success: false, error: 'Video processing failed' };
                }

                attempts++;
            }

            if (!isReady) {
                setStepStatus('processing', 'error');
                updateCurrentFileProgress({
                    currentStep: 'error',
                    errorMessage: 'Processing timeout'
                });
                return { success: false, error: 'Processing timeout' };
            }

            // Phase 3: Finalise search indexing
            setStepStatus('indexing', 'active');
            updateCurrentFileProgress({
                currentStep: 'indexing'
            });

            const finaliseResp = await finaliseVideo(
                assistantId,
                videoId,
                filename,
                fileSize
            );

            if (!finaliseResp.ok) {
                setStepStatus('indexing', 'error');
                updateCurrentFileProgress({
                    currentStep: 'error',
                    errorMessage: 'Finalisation failed'
                });
                return { success: false, error: 'Finalisation failed' };
            }

            // All steps complete
            setStepStatus('indexing', 'complete');
            updateCurrentFileProgress({
                currentStep: 'complete'
            });

            await new Promise(resolve => setTimeout(resolve, 1500));

            return { success: true };

        } catch (error) {
            const currentStep = currentFileProgress?.currentStep || 'uploading';
            const stepKey = currentStep === 'complete' || currentStep === 'error'
                ? 'uploading'
                : currentStep;

            setStepStatus(stepKey, 'error');
            updateCurrentFileProgress({
                currentStep: 'error',
                errorMessage: error instanceof Error ? error.message : 'Unknown error'
            });
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error'
            };
        }
    };

    const resetProgress = () => {
        setCurrentFileProgress(null);
        setCurrentFileIndex(0);
        setTotalFiles(0);
    };

    return {
        currentFileProgress,
        currentFileIndex,
        totalFiles,
        uploadDocument,
        uploadVideo,
        resetProgress
    };
};
